import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import RichSelectField from '../../components/formfields/RichSelectField';

// Country options
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'JP', name: 'Japan' },
  { code: 'SG', name: 'Singapore' },
  { code: 'NZ', name: 'New Zealand' },
];

const BillingAddressSelector = ({
  isVisible,
  billingCountry,
  billingZipCode,
  onCountryChange,
  onZipCodeChange,
  validationErrors,
  countryLabel,
  zipCodeLabel,
  countryPlaceholder,
  zipCodePlaceholder,
  required
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (!isVisible) {
    return null;
  }

  const labelClassName = clsx(
    'text-sm mb-2 font-medium',
    {
      'text-slate-200': isDark,
      'text-gray-500': !isDark
    }
  );

  const inputClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg',
    {
      'text-slate-200': isDark,
      'text-gray-500': !isDark
    }
  );

  // Get dynamic placeholder based on selected country
  const getZipPlaceholder = () => {
    if (zipCodePlaceholder) return zipCodePlaceholder;
    return billingCountry === 'US' ? '12345' : 'Postal Code';
  };

  return (
    <div className="mb-5">
      <div className="flex flex-col md:grid md:grid-cols-2 gap-3">
        {/* Country Select */}
        <div className="flex flex-col">
          <RichSelectField
            id="billing-country"
            label={countryLabel}
            value={billingCountry}
            valueKey="code"
            onChange={(e) => {
              const value = e.target.value;
              onCountryChange?.(value);
            }}
            onBlur={(e) => {
              const value = e.target.value;
              onCountryChange?.(value);
            }}
            options={COUNTRIES}
            formatValue={(option) => ({
              key: option?.code,
              icon: null,
              label: option?.name || '',
              value: option?.code
            })}
            placeholder={countryPlaceholder}
            required={required}
            error={validationErrors?.billingCountry}
          />
        </div>

        {/* Zip Code Input */}
        <div className="flex flex-col">
          <label className={labelClassName}>
            {zipCodeLabel} {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            placeholder={getZipPlaceholder()}
            value={billingZipCode || ''}
            onChange={(e) => onZipCodeChange?.(e.target.value)}
            onBlur={(e) => onZipCodeChange?.(e.target.value)}
            className={clsx(
              inputClassName,
              validationErrors?.billingZipCode ? 'border-red-300' : 'border-gray-300',
              'focus:outline-none focus:border-blue-500'
            )}
          />
          {validationErrors?.billingZipCode && (
            <p className="mt-1 text-sm text-red-600">
              {validationErrors.billingZipCode}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

BillingAddressSelector.propTypes = {
  /**
   * Whether this component should be visible
   */
  isVisible: PropTypes.bool.isRequired,

  /**
   * Selected billing country code (e.g., 'US', 'CA')
   */
  billingCountry: PropTypes.string,

  /**
   * Billing zip/postal code
   */
  billingZipCode: PropTypes.string,

  /**
   * Callback when country is changed
   * @param {Object} country - Country object with code and name
   */
  onCountryChange: PropTypes.func.isRequired,

  /**
   * Callback when zip code is changed
   * @param {string} zipCode - The new zip code value
   */
  onZipCodeChange: PropTypes.func.isRequired,

  /**
   * Validation errors object
   */
  validationErrors: PropTypes.shape({
    billingCountry: PropTypes.string,
    billingZipCode: PropTypes.string,
  }),

  /**
   * Label for country field
   */
  countryLabel: PropTypes.string,

  /**
   * Label for zip code field
   */
  zipCodeLabel: PropTypes.string,

  /**
   * Placeholder for country select
   */
  countryPlaceholder: PropTypes.string,

  /**
   * Placeholder for zip code input
   */
  zipCodePlaceholder: PropTypes.string,

  /**
   * Whether fields are required
   */
  required: PropTypes.bool,
};

BillingAddressSelector.defaultProps = {
  billingZipCode: '',
  validationErrors: {},
  countryLabel: 'Billing country',
  zipCodeLabel: 'Billing zip code',
  countryPlaceholder: 'Select country',
  zipCodePlaceholder: null,
  required: true,
};

export default BillingAddressSelector;